import React, { useState } from 'react';
import { Box, Typography, Divider } from '@mui/material';
import MouseIcon from '@mui/icons-material/Mouse';
import { useTranslation } from 'react-i18next';
import { UserProfile } from '../../types/UserProfile';
import { SettingRadioGroup, SettingToggleCard } from '../common/SettingCard';
import { isRightClickMenuEnabled, setRightClickMenuEnabled } from '../../utils/gameCardPrefs';

interface LibrarySettingsProps {
  profile: UserProfile;
  onSave: (updatedProfile: UserProfile) => Promise<void>;
}

const LibrarySettings: React.FC<LibrarySettingsProps> = ({ profile, onSave }) => {
  const { t } = useTranslation('settings');
  const [order, setOrder] = useState<'ps' | 'up'>(profile.personalize?.l?.o ?? 'ps');
  // 端末ローカル設定（localStorage・即時反映）。並び順のサーバー保存とは独立。
  const [rightClickMenu, setRightClickMenu] = useState<boolean>(isRightClickMenuEnabled);

  // 並び順は選択した瞬間にサーバー保存（楽観更新＋失敗時ロールバック）。
  // 保存ボタンを廃止し、同じタブ内のトグルと同様「変更＝即時反映」で統一する。
  const handleOrderChange = (newOrder: 'ps' | 'up') => {
    const prev = order;
    setOrder(newOrder);
    const personalize = { ...profile.personalize };
    if (newOrder === 'up') {
      personalize.l = { o: 'up' };
    } else {
      // 'ps'（デフォルト）はキーを持たせない（キー無し＝'ps'）
      delete personalize.l;
    }
    onSave({ ...profile, personalize }).catch((error) => {
      console.error('ライブラリ設定の更新に失敗しました', error);
      setOrder(prev); // ロールバック
    });
  };

  const handleRightClickToggle = (on: boolean) => {
    setRightClickMenu(on);
    setRightClickMenuEnabled(on);
  };

  const ORDER_OPTIONS = [
    { value: 'ps', label: t('library.orderPsLabel'), description: t('library.orderPsDesc') },
    { value: 'up', label: t('library.orderUpLabel'), description: t('library.orderUpDesc') },
  ];

  return (
    <Box sx={{ width: '100%', maxWidth: 600 }}>
      <Typography variant="h6" gutterBottom>
        {t('library.orderTitle')}
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        {t('library.orderDesc')}
      </Typography>
      <SettingRadioGroup
        name="library-order"
        value={order}
        onChange={(v) => handleOrderChange(v as 'ps' | 'up')}
        options={ORDER_OPTIONS}
      />

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" gutterBottom>
        {t('library.cardSection')}
      </Typography>
      <SettingToggleCard
        icon={<MouseIcon sx={{ fontSize: 20 }} />}
        title={t('library.rightClickTitle')}
        description={t('library.rightClickDesc')}
        checked={rightClickMenu}
        onToggle={handleRightClickToggle}
      />
    </Box>
  );
};

export default LibrarySettings;
